import { StandardEvents } from '@shopify/events';

const cartTriggerSelector = '.kv-actions [aria-label="Cart"]';
const cartCountSelector = '.kv-cart-count';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const clampCartCount = (count) => Math.max(0, Number.parseInt(String(count), 10) || 0);

const animateCartCount = (bubble) => {
  if (reducedMotion.matches || typeof bubble.animate !== 'function') return;

  bubble.getAnimations().forEach((animation) => animation.cancel());
  bubble.animate(
    [
      { transform: 'scale(0.72)', opacity: 0.55 },
      { transform: 'scale(1.18)', opacity: 1, offset: 0.58 },
      { transform: 'scale(1)', opacity: 1 },
    ],
    {
      duration: 360,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    }
  );
};

const renderCartCount = (count, { animate = true } = {}) => {
  const nextCount = clampCartCount(count);

  document.querySelectorAll(cartTriggerSelector).forEach((trigger) => {
    let bubble = trigger.querySelector(cartCountSelector);

    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'kv-cart-count';
      bubble.setAttribute('aria-hidden', 'true');
      trigger.appendChild(bubble);
    }

    bubble.hidden = nextCount === 0;
    bubble.textContent = nextCount > 99 ? '99+' : nextCount > 0 ? String(nextCount) : '';

    if (animate && nextCount > 0) animateCartCount(bubble);
  });
};

const getProductForm = (event) => {
  if (!(event.target instanceof Element)) return null;
  if (event.target.matches('product-form-component')) return event.target;
  return event.target.closest('product-form-component');
};

const setAddToCartPending = (productForm, pending) => {
  if (!productForm) return;

  productForm.querySelectorAll('add-to-cart-component').forEach((container) => {
    const button = container.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (pending) {
      // The stock click handler starts its success animation optimistically.
      // Clear it now and replay it only after Shopify confirms the cart update.
      button.removeAttribute('data-added');
      button.dataset.kvCartPending = 'true';
      button.setAttribute('aria-busy', 'true');
    } else {
      delete button.dataset.kvCartPending;
      button.removeAttribute('aria-busy');
    }
  });
};

const showConfirmedAddToCart = (productForm) => {
  if (!productForm) return;

  productForm.querySelectorAll('add-to-cart-component').forEach((container) => {
    const button = container.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.removeAttribute('data-added');
    requestAnimationFrame(() => {
      if (typeof container.animateAddToCart === 'function') {
        container.animateAddToCart();
      } else {
        button.dataset.added = 'true';
        window.setTimeout(() => button.removeAttribute('data-added'), 1200);
      }
    });
  });
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') return;

  const productForm = getProductForm(event);
  setAddToCartPending(productForm, true);

  promise
    .then(({ cart, detail }) => {
      setAddToCartPending(productForm, false);
      if (detail?.didError) return;

      const totalQuantity = cart?.totalQuantity;
      const fallbackCount = detail?.itemCount;
      renderCartCount(totalQuantity ?? fallbackCount ?? 0);
      showConfirmedAddToCart(productForm);

    })
    .catch(() => setAddToCartPending(productForm, false));
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (cart) renderCartCount(cart.item_count, { animate: false });
    })
    .catch(() => {});
});
